import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import React, { useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import L from "leaflet";
import "../styles/Geothermal.css";
import stationData from "./Station-Charging.json";

// ✅ Fuel type icons
const fuelIcons = {
  CNG: "/img/cng2.webp",
  ELEC: "/img/ele.webp",
  BD: "/img/bd1.webp",
  LPG: "/img/lpg1.webp",
  E85: "/img/e85.png",
};

const isMobileView = () => window.innerWidth <= 778;

const createIcon = (fuelType) => {
  const iconUrl = fuelIcons[fuelType?.toUpperCase()] || "/img/default.png";
  return new L.Icon({
    iconUrl,
    iconSize: [35, 41],
    iconAnchor: [12, 41],
    popupAnchor: isMobileView() ? [0, 10] : [1, -34],
  });
};

// Helper component to pan the map
const MapFocus = ({ location }) => {
  const map = useMap();
  if (location) {
    map.flyTo(location, isMobileView() ? 5 : 10);
  }
  return null;
};

function UtahChargingStation() {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const markerRefs = useRef([]);
  const [searchTerm, setSearchTerm] = useState("");

  const handleLocationClick = (location, index) => {
    setSelectedLocation(location);
    if (markerRefs.current[index]) {
      markerRefs.current[index].openPopup();
    }
  };

  const filteredData = stationData.filter(
    (spot) =>
      spot.ZIP.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spot["Station Name"].toLowerCase().includes(searchTerm.toLowerCase()) ||
      spot.City.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spot["Fuel Type Code"].toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <LandingSection
        data={{
          imageName: "charging_station.webp",
          title: "Utah Charging Stations",
          visible: "",
        }}
      />

      <div className="verticals_section">
        <div className="verticals-intro">
          <div className="verticals-intro-sec1">
            <h2>Utah’s EV Charging Stations ({filteredData.length})</h2>
          </div>
          <div className="verticals-intro-sec2">
            <p>
              Utah is building a strong EV charging network, with fast chargers
              and public stations expanding across cities and highways.
            </p>
            <Link to={"/about"}></Link>
          </div>
        </div>
      </div>

      <div className="geothermal-map">
        {/* Left-side list */}
        <div className="list-container">
          <h2>
            Charging Stations ({filteredData.length}) <br />
            <span className="text-sm font-base">
              (*Not all stations are marked, only those with fully verified data
              are displayed.)
              <div className="mt-2 flex flex-col items-end gap-1">
                <span className="font-semibold text-gray-700">Data Sources:</span>
                <a
                  target="_blank"
                  href="https://afdc.energy.gov/fuels/electricity-locations#/analyze?region=US-UT"
                  rel="noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Charging Station Source
                </a>
                <a
                  target="_blank"
                  href="https://afdc.energy.gov/states/ut"
                  rel="noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  AFDC - Utah State Data
                </a>
              </div>
            </span>
          </h2>

          {/* Search */}
          <div className="flex justify-center items-center my-4">
            <input
              type="text"
              placeholder="Search by Fuel Type, Zip, Station Name or City"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input p-2 border border-gray-300 rounded-3xl w-[40rem]"
            />
          </div>

          {/* Header */}
          <div className="list-item-content rounded-lg border-pink-700 hover:border-1 border-solid p-4 transition-all duration-200 bg-red-200/50">
            <span className="text-red-800 font-bold">Station Name</span>
            <span className="text-red-800 font-bold">City</span>
            <span className="text-red-800 font-bold">Zip Code</span>
            <span className="text-red-800 font-bold">Fuel Type</span>
          </div>

          {/* Scrollable List */}
          <div className="list-scroll max-h-[600px] overflow-y-auto mt-2">
            <ul>
              {filteredData
                .filter((spot) => spot["Intersection Directions"])
                .map((spot, index) => (
                  <li
                    key={index}
                    className="list-item"
                    onClick={() =>
                      handleLocationClick(
                        {
                          Latitude: spot.Latitude,
                          Longitude: spot.Longitude,
                          details: spot,
                        },
                        index
                      )
                    }
                  >
                    <div className="list-item-content text-blue-800 font-bold">
                      <span>{spot["Station Name"]}</span>
                      <span>{spot.City || `Hotspot ${index + 1}`}</span>
                      <span>{spot.ZIP}</span>
                      <span>
                        <img
                          src={
                            fuelIcons[spot["Fuel Type Code"].toUpperCase()] ||
                            "/img/default.png"
                          }
                          alt={spot["Fuel Type Code"]}
                          title={spot["Fuel Type Code"]}
                          className="w-6 h-6"
                        />
                      </span>
                    </div>
                  </li>
                ))}
            </ul>
          </div>
        </div>

        {/* Map */}
        <div className="map-container">
          <MapContainer
            center={[39.32098, -111.093731]}
            zoom={7}
            minZoom={6}
            maxBounds={[
              [30, -105],
              [50, -120],
            ]}
            maxBoundsViscosity={1.0}
            className="map"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {selectedLocation && (
              <MapFocus
                location={[
                  selectedLocation.Latitude,
                  selectedLocation.Longitude,
                ]}
              />
            )}

            {filteredData
              .filter((spot) => spot["Intersection Directions"])
              .map((spot, index) => (
                <Marker
                  key={index}
                  position={[spot.Latitude, spot.Longitude]}
                  icon={createIcon(spot["Fuel Type Code"])}
                  ref={(el) => (markerRefs.current[index] = el)}
                >
                  <Popup>
                    <b>
                      <u>Charging Station</u>
                    </b>
                    <br />
                    Fuel Type: {spot["Fuel Type Code"]} <br />
                    Station Name: {spot["Station Name"]} <br />
                    Street Address: {spot["Street Address"]}
                    <br />
                    City: {spot.City}
                    <br />
                    State: {spot.State}
                    <br />
                    Zip Code: {spot.ZIP}
                    <br />
                    Station Phone: {spot["Station Phone"]}
                    <br />
                    Status Code: {spot["Status Code"]}
                  </Popup>
                </Marker>
              ))}
          </MapContainer>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default UtahChargingStation;
